import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';
import SpendingSummaryCard from './components/SpendingSummaryCard';
import TransactionHistory from './components/TransactionHistory';
import BudgetManager from './components/BudgetManager';
import SpendingInsights from './components/SpendingInsights';
import BillTracker from './components/BillTracker';
import SpendingCalendar from './components/SpendingCalendar';
import Icon from '../../components/AppIcon';

const PaymentSpendingHub = () => {
  const { userProfile } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const spendingSummary = {
    totalSpent: 4285.67,
    budgetLimit: 5000,
    transactions: 142,
    topCategory: 'Shopping',
    change: -12.3,
    breakdown: [
      { category: 'Shopping', amount: 1245.32, percentage: 29, color: '#3B82F6' },
      { category: 'Food & Dining', amount: 892.45, percentage: 21, color: '#10B981' },
      { category: 'Transportation', amount: 654.28, percentage: 15, color: '#F59E0B' },
      { category: 'Entertainment', amount: 523.18, percentage: 12, color: '#8B5CF6' },
      { category: 'Bills & Utilities', amount: 485.22, percentage: 11, color: '#EF4444' },
      { category: 'Other', amount: 485.22, percentage: 12, color: '#6B7280' }
    ]
  };

  const transactions = [
    {
      id: 1,
      merchant: 'Amazon',
      category: 'Shopping',
      amount: -89.99,
      date: '2026-01-17',
      time: '14:32',
      status: 'completed',
      paymentMethod: 'BTC',
      icon: 'ShoppingBag',
      receipt: true
    },
    {
      id: 2,
      merchant: 'Starbucks',
      category: 'Food & Dining',
      amount: -12.45,
      date: '2026-01-17',
      time: '09:15',
      status: 'completed',
      paymentMethod: 'Card',
      icon: 'Coffee',
      receipt: false
    },
    {
      id: 3,
      merchant: 'Uber',
      category: 'Transportation',
      amount: -24.50,
      date: '2026-01-16',
      time: '18:45',
      status: 'completed',
      paymentMethod: 'USDC',
      icon: 'Car',
      receipt: true
    },
    {
      id: 4,
      merchant: 'Netflix',
      category: 'Entertainment',
      amount: -15.99,
      date: '2026-01-15',
      time: '00:01',
      status: 'completed',
      paymentMethod: 'Card',
      icon: 'Tv',
      receipt: false
    },
    {
      id: 5,
      merchant: 'Electric Company',
      category: 'Bills & Utilities',
      amount: -125.00,
      date: '2026-01-15',
      time: '10:00',
      status: 'completed',
      paymentMethod: 'ETH',
      icon: 'Zap',
      receipt: true
    },
    {
      id: 6,
      merchant: 'Whole Foods',
      category: 'Food & Dining',
      amount: -67.32,
      date: '2026-01-14',
      time: '16:20',
      status: 'completed',
      paymentMethod: 'Card',
      icon: 'ShoppingCart',
      receipt: true
    }
  ];

  const budgets = [
    {
      category: 'Shopping',
      limit: 1500,
      spent: 1245.32,
      percentage: 83,
      alert: 'warning',
      icon: 'ShoppingBag',
      color: '#3B82F6'
    },
    {
      category: 'Food & Dining',
      limit: 1000,
      spent: 892.45,
      percentage: 89,
      alert: 'warning',
      icon: 'Coffee',
      color: '#10B981'
    },
    {
      category: 'Transportation',
      limit: 800,
      spent: 654.28,
      percentage: 82,
      alert: 'warning',
      icon: 'Car',
      color: '#F59E0B'
    },
    {
      category: 'Entertainment',
      limit: 600,
      spent: 523.18,
      percentage: 87,
      alert: 'warning',
      icon: 'Tv',
      color: '#8B5CF6'
    },
    {
      category: 'Bills & Utilities',
      limit: 500,
      spent: 485.22,
      percentage: 97,
      alert: 'critical',
      icon: 'Zap',
      color: '#EF4444'
    }
  ];

  const upcomingBills = [
    {
      id: 1,
      name: 'Internet Service',
      amount: 79.99,
      dueDate: '2026-01-22',
      status: 'upcoming',
      autoPay: true,
      icon: 'Wifi'
    },
    {
      id: 2,
      name: 'Phone Bill',
      amount: 55.00,
      dueDate: '2026-01-25',
      status: 'upcoming',
      autoPay: true,
      icon: 'Smartphone'
    },
    {
      id: 3,
      name: 'Gym Membership',
      amount: 49.99,
      dueDate: '2026-01-28',
      status: 'upcoming',
      autoPay: false,
      icon: 'Dumbbell'
    },
    {
      id: 4,
      name: 'Rent',
      amount: 1500.00,
      dueDate: '2026-02-01',
      status: 'upcoming',
      autoPay: false,
      icon: 'Home'
    }
  ];

  const insights = [
    {
      id: 1,
      type: 'savings',
      title: 'Save $45/month on subscriptions',
      description: 'You have 3 unused subscriptions that could be cancelled',
      action: 'Review subscriptions',
      icon: 'TrendingDown',
      color: '#10B981'
    },
    {
      id: 2,
      type: 'alert',
      title: 'Shopping budget at 83%',
      description: 'You\'re approaching your monthly shopping limit',
      action: 'View budget',
      icon: 'AlertTriangle',
      color: '#F59E0B'
    },
    {
      id: 3,
      type: 'tip',
      title: 'Use crypto for larger purchases',
      description: 'Save 2-3% in fees by paying with BTC or ETH',
      action: 'Learn more',
      icon: 'Lightbulb',
      color: '#3B82F6'
    },
    {
      id: 4,
      type: 'achievement',
      title: 'Spending down 12% this month',
      description: 'Great job! You\'re on track to save $200',
      action: 'View trends',
      icon: 'Award',
      color: '#8B5CF6'
    }
  ];

  return (
    <MainLayout>
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Payment & Spending Hub</h1>
              <p className="text-muted-foreground">Track transactions, manage budgets, and analyze spending patterns</p>
            </div>
            {userProfile && (
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Spending Limit</p>
                <p className="text-2xl font-bold text-foreground">${userProfile?.spending_limit?.toLocaleString() || '0'}</p>
                {userProfile?.tier === 'pro' && (
                  <div className="inline-flex items-center gap-1 mt-1 px-2 py-1 bg-primary/20 text-primary rounded text-xs font-semibold">
                    <Icon name="Crown" size={12} />
                    Pro Tier
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2">
              <SpendingSummaryCard data={spendingSummary} />
            </div>
            <div>
              <SpendingInsights insights={insights} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2">
              <BudgetManager budgets={budgets} />
            </div>
            <div>
              <BillTracker bills={upcomingBills} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <TransactionHistory 
              transactions={transactions} 
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
            />
          </div>

          <div className="grid grid-cols-1 gap-6">
            <SpendingCalendar transactions={transactions} />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default PaymentSpendingHub;